import * as THREE from "three";
import { SCENE_DEFAULTS } from "@/shared/config/constants";

// Strength of the relief maps. The supplied maps are low-amplitude, so these
// are >1 to make the relief readable; tune to taste.
const NORMAL_SCALE = SCENE_DEFAULTS.normalScale;
const BUMP_SCALE = SCENE_DEFAULTS.bumpScale;

/**
 * Generates planar UVs for a geometry that has none, projecting along its
 * thinnest axis. `scale` maps local model units to UV units, so the texture
 * tiles at a consistent real-world size. Aspect ratio is preserved.
 */
function generatePlanarUV(geometry: THREE.BufferGeometry, scale: number): void {
  const pos = geometry.attributes.position;
  if (!pos) return;

  geometry.computeBoundingBox();
  const size = new THREE.Vector3();
  geometry.boundingBox!.getSize(size);

  // Project along the thinnest axis; use the other two for U and V.
  const dims = [size.x, size.y, size.z];
  const thin = dims.indexOf(Math.min(...dims));
  const [au, av] = thin === 0 ? [1, 2] : thin === 1 ? [0, 2] : [0, 1];
  const comp = (i: number, axis: number) =>
    axis === 0 ? pos.getX(i) : axis === 1 ? pos.getY(i) : pos.getZ(i);

  const uv = new Float32Array(pos.count * 2);
  for (let i = 0; i < pos.count; i++) {
    uv[i * 2] = comp(i, au) * scale;
    uv[i * 2 + 1] = comp(i, av) * scale;
  }
  geometry.setAttribute("uv", new THREE.BufferAttribute(uv, 2));
}

type ApplyTextureParams = {
  model: THREE.Group;
  // Base color (albedo) texture URL.
  texture: string;
  // Model node names to apply the texture to (partial, case-insensitive match).
  applyTo: string[];
  // Optional secondary map URLs.
  normalMap?: string;
  bumpMap?: string;
  reflexMap?: string; // grayscale roughness map
  // Tiling controls (per-config). Span in model units covered by one tile on
  // UV-less meshes, and how many times the texture repeats on UV'd meshes.
  tileSize: number;
  repeat: number;
  loadTexture: (url: string) => Promise<THREE.Texture>;
  configureFabricMap: (map: THREE.Texture) => void;
  findNodesByName: (name: string) => THREE.Object3D[];
  onTextureApplied?: (texture: THREE.Texture) => void;
};

type TexturableMaterial =
  | THREE.MeshStandardMaterial
  | THREE.MeshPhysicalMaterial;

type ConfiguratorMesh = THREE.Mesh<
  THREE.BufferGeometry,
  THREE.Material | THREE.Material[]
>;

function isConfiguratorMesh(node: THREE.Object3D): node is ConfiguratorMesh {
  return node instanceof THREE.Mesh;
}

function isTexturableMaterial(
  material: THREE.Material,
): material is TexturableMaterial {
  return (
    material instanceof THREE.MeshStandardMaterial ||
    material instanceof THREE.MeshPhysicalMaterial
  );
}

function getMaterials(material: THREE.Material | THREE.Material[]) {
  return Array.isArray(material) ? material : [material];
}

/**
 * Applies a texture (and optional normal/bump/roughness maps) to the model
 * nodes named in `applyTo`.
 */
export async function applyTexture(params: ApplyTextureParams): Promise<void> {
  const {
    model,
    texture,
    applyTo,
    normalMap,
    bumpMap,
    reflexMap,
    tileSize,
    repeat,
    loadTexture,
    configureFabricMap,
    findNodesByName,
    onTextureApplied,
  } = params;

  if (!model || !texture || !applyTo.length) return;

  try {
    // Collect target nodes from the option's `applyTo` tokens, deduped.
    const nodes = Array.from(
      new Set(applyTo.flatMap((name) => findNodesByName(name))),
    );

    // The model is in millimetres, so `tileSize` is the span (in mm) that one
    // texture tile should cover.
    const TILE_SIZE = tileSize;

    // Some meshes (e.g. the legs) ship without UVs, so the texture has nothing
    // to map onto and renders as a flat average color. Generate planar UVs for
    // them. `repeat` multiplies the UVs, so pre-divide by it to keep the
    // effective tiling at TILE_SIZE.
    const unifiedRepeat = repeat;
    const uvScale = 1 / (TILE_SIZE * unifiedRepeat);
    for (const node of nodes) {
      node.traverse((child) => {
        if (
          isConfiguratorMesh(child) &&
          child.geometry &&
          !child.geometry.attributes.uv
        ) {
          generatePlanarUV(child.geometry, uvScale);
        }
      });
    }

    // Load + configure a map: shared wrap/filter, matched tiling, correct color
    // space. Color maps are sRGB; data maps (normal/bump/roughness) are linear.
    const prepareMap = async (
      url: string | undefined,
      colorSpace: THREE.ColorSpace,
    ): Promise<THREE.Texture | null> => {
      if (!url) return null;
      const tex = await loadTexture(url);
      configureFabricMap(tex);
      tex.colorSpace = colorSpace;
      tex.repeat.set(unifiedRepeat, unifiedRepeat);
      onTextureApplied?.(tex);
      return tex;
    };

    const [map, normalTex, bumpTex, roughTex] = await Promise.all([
      prepareMap(texture, THREE.SRGBColorSpace),
      prepareMap(normalMap, THREE.NoColorSpace),
      prepareMap(bumpMap, THREE.NoColorSpace),
      prepareMap(reflexMap, THREE.NoColorSpace),
    ]);
    if (!map) return;

    // Store old base textures before replacing
    const oldTextures = new Set<THREE.Texture>();
    for (const node of nodes) {
      node.traverse((child) => {
        if (!isConfiguratorMesh(child)) return;

        for (const material of getMaterials(child.material)) {
          if (
            isTexturableMaterial(material) &&
            material.map instanceof THREE.Texture
          ) {
            oldTextures.add(material.map);
          }
        }
      });
    }

    // Apply maps.
    // The GLB ships without materials, so three.js assigns ONE shared default
    // material to every mesh. Mutating it directly would texture the whole model
    // (and later options would overwrite earlier ones). So we give each target
    // mesh its own material clone the first time we touch it, then reuse it.
    // Every slot is set explicitly so switching options clears stale maps.
    const ownMaterial = (material: THREE.Material): THREE.Material => {
      if (!isTexturableMaterial(material)) return material;

      let ownedMaterial = material;
      if (!ownedMaterial.userData.__configuratorOwned) {
        ownedMaterial = material.clone();
        ownedMaterial.userData = {
          ...ownedMaterial.userData,
          __configuratorOwned: true,
        };
      }
      ownedMaterial.map = map;
      ownedMaterial.normalMap = normalTex;
      // The supplied normal maps are low-amplitude, so amplify to make the
      // relief readable. Tune NORMAL_SCALE / BUMP_SCALE to taste.
      if (normalTex)
        ownedMaterial.normalScale = new THREE.Vector2(
          NORMAL_SCALE,
          NORMAL_SCALE,
        );
      ownedMaterial.bumpMap = bumpTex;
      if (bumpTex) ownedMaterial.bumpScale = BUMP_SCALE;
      ownedMaterial.roughnessMap = roughTex;
      // Enforce non-metallic, fully rough surface on every textured part too
      // (clones don't reliably carry the values set at model load).
      ownedMaterial.metalness = 0;
      ownedMaterial.roughness = 1;
      ownedMaterial.needsUpdate = true;
      return ownedMaterial;
    };

    for (const node of nodes) {
      node.traverse((child) => {
        if (!isConfiguratorMesh(child)) return;
        child.material = Array.isArray(child.material)
          ? child.material.map(ownMaterial)
          : ownMaterial(child.material);
      });
    }

    // Dispose old base textures that are no longer used
    oldTextures.forEach((tex) => {
      if (tex !== map) {
        tex.dispose();
      }
    });
  } catch (error) {
    console.error("Failed to apply texture:", error);
  }
}
